'use client';

import {ArrowUpRightFromSquareIcon, Button, Link, Modal, Stack, Text} from '@eds-web/react';
import {useLocale, useMessages} from '@epic-mw/i18n';
import {useEffect} from 'react';
import {useQuickLinks} from '@/hooks';
import {AnalyticEventProps, PurchaseReceipt} from '@/types';
import {emitEvent} from '@/lib/utils/analytics';
import {css} from '@panda/css';
import {usePurchaseContext} from './PurchaseProvider';
import {ModalLoading} from '@/components/molecules/loading/ModalLoading';
import {LoadingSpinner} from '@/components/atoms';
import {useRouter} from 'next/navigation';
import {Box} from '@panda/jsx';
import {formatCurrency} from '@/lib/utils/giftCard.utils';
import {getMessage} from '@/lib/utils/i18n.utils';
import {applySetLangToHref} from '@/lib/utils/setLang.utils';

const modalStyles = css({
	alignItems: 'center',
	textAlign: 'center',
	'& > div': {
		justifyContent: 'center !important'
	},
	smDown: {
		width: 'unset !important'
	}
});

interface PostPurchaseModalProps extends PurchaseReceipt {
	open: boolean;
	isLoading?: boolean;
	analytics?: AnalyticEventProps;
	onClose?: () => void;
}

export const PostPurchaseModal = ({
	open,
	Offers,
	TransactionId,
	earnedEpicRewards,
	formattedEarnedEpicRewards,
	isLoading,
	onClose
}: PostPurchaseModalProps) => {
	const locale = useLocale();
	const messages = useMessages();
	const {offerType} = usePurchaseContext();
	const quickLinks = useQuickLinks('itemShop');
	const count = Offers?.length;
	const router = useRouter();

	useEffect(() => {
		const timer = setTimeout(() => {
			router.push(applySetLangToHref(quickLinks.itemShop));
		}, 3000);

		return () => clearTimeout(timer);
	}, [router, quickLinks.itemShop]);

	useEffect(() => {
		if (!open) return;
		emitEvent(
			{
				eventCategory: 'pageview',
				interactionType: 'pageview',
				eventLabel: 'PageView_V2',
				eventValue: {
					subView: 'Post Purchase Confirmed',
					locale,
					offerType,
					offerCount: count,
					status: 'success'
				}
			},
			'PageView'
		);
	}, [open]);

	return (
		<Modal
			className={modalStyles}
			hideXButton
			size="sm"
			containerProps={{className: css({zIndex: 9999})}}
			isOpen={open}
			onOpenChange={(isOpen) => {
				// seems like this is never called when `isOpen === true` :think:
				if (!isOpen) onClose?.();
			}}
			title={
				<Box pt="10px">
					<LoadingSpinner />
				</Box>
			}
			aria-labelledby={messages['epic.app.cart.success.title']}
			closeLabel={messages['epic.app.close'] || 'Close'}
			closeButton={undefined}
			content={
				<>
					{isLoading && <ModalLoading />}
					<Stack gap={24}>
						<Stack gap={8}>
							<Text variant="paragraphXS" color="secondary">
								{getMessage(messages, 'epic.app.cart.success.order_number', [
									TransactionId
								])}
							</Text>

							<Text variant="headingLG" color="primary">
								{messages['epic.app.cart.success.title']}
							</Text>

							<Text variant="uiMDMedium" color="secondary">
								{messages['epic.app.cart.success.redirect']}
							</Text>
						</Stack>
						<Stack gap={16}>
							<Text variant="uiMDMedium" color="secondary">
								{messages['epic.app.cart.success.redirect.takingLong']}
							</Text>

							<Button
								fullWidth
								color="primary"
								size="lg"
								as={Link}
								href={applySetLangToHref(quickLinks.itemShop)}
								style={{
									textDecoration: 'none',
									color: 'black',
									cursor: 'pointer',
									margin: 'auto',
									minWidth: '100%',
									fontWeight: 'normal'
								}}>
								{messages['epic.app.cart.success.cta.itemShop']}{' '}
								<ArrowUpRightFromSquareIcon />
							</Button>
						</Stack>

						{earnedEpicRewards && formattedEarnedEpicRewards && (
							<Stack
								gap={8}
								className={css({
									bg: '#28282C',
									py: 'eds.dimension.12',
									px: 'eds.dimension.16',
									borderRadius: 'eds.dimension.8'
								})}>
								<Box
									display="flex"
									justifyContent="space-between"
									alignItems="center">
									<Text variant="uiSMBold" color="primary">
										{messages['epic.app.cart.success.rewards']}
									</Text>
									<Box
										py="eds.dimension.6"
										px="eds.dimension.12"
										border="1px solid #FFFFFF59"
										borderRadius="eds.borderRadius.full"
										borderWidth="1px"
										lineHeight={0}>
										<Text variant="uiSM" color="primary">
											{formatCurrency(
												earnedEpicRewards.amount,
												earnedEpicRewards.currency,
												locale,
												formattedEarnedEpicRewards.decimals!
											)}
										</Text>
									</Box>
								</Box>

								<Text variant="uiSM" color="secondary" style={{textAlign: 'left'}}>
									{messages['epic.app.cart.success.rewards.desc']}
								</Text>
							</Stack>
						)}
					</Stack>
				</>
			}
		/>
	);
};
